import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { endServerSession } from '../../api/logout';
import { LOGIN_SESSION_END } from '../../modules/analytics/events';
import { trackEvent } from '../../modules/analytics/trackingMiddleware';
import { forgetThisBrowser } from '../../modules/close-the-loop';
import { PASSCODE_ROUTES } from '../../navigation/paths';
import { endSessionHere } from '../../session';

export interface LogoutOptions {
  readonly navigateTo?: string | null;
}

export interface LogoutAction {
  readonly logOut: () => void;
  /** The server is ending the session; the person stays signed in until it has. */
  readonly signingOut: boolean;
}

const LOGOUT_MUTATION_KEY = ['session', 'logout'] as const;

/**
 * Ends the session on the server first. Its httpOnly cookie is the only thing that
 * signs this browser in, so the tab signs out — and tells the app's other tabs —
 * only once the server has ended it. If it cannot, the person stays signed in and
 * is told, rather than shown a sign-out the next reload would undo. The tab's
 * session sync forgets what was held for the person as the session ends here.
 */
export const useLogout = ({ navigateTo = PASSCODE_ROUTES.login }: LogoutOptions = {}): LogoutAction => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate, isPending } = useMutation({
    mutationKey: LOGOUT_MUTATION_KEY,
    mutationFn: () => forgetThisBrowser().then(endServerSession),
    onSuccess: () => {
      trackEvent(LOGIN_SESSION_END, { url: window.location.href });
      endSessionHere(queryClient);
      if (navigateTo !== null) navigate(navigateTo, { replace: true });
    },
    onError: (error: unknown) => {
      captureException(error, { tags: { feature: 'logout' } });
      toast.error(t('logout.failed', 'Couldn’t sign you out. Check your connection and try again.'));
    },
  });

  return {
    logOut: () => {
      if (queryClient.isMutating({ mutationKey: LOGOUT_MUTATION_KEY }) > 0) return;
      mutate();
    },
    signingOut: isPending,
  };
};
